import { ManualVisit, UnifiedVisit } from "@/interfaces";
import api from "@/lib/axios";
import { DefaultGetRequestParams, DefaultResponse } from "./types";

interface VisitsGetRequestParams extends DefaultGetRequestParams {}

interface UpdateVisitObjectivePayload {
  visit_objective_id: number | null;
}

interface VisitInput {
  reference_point_id: number;
  visit_objective_id?: number | null;
  start_datetime: string; // formato: yyyy-MM-dd HH:mm:ss
  end_datetime: string; // formato: yyyy-MM-dd HH:mm:ss
  notes?: string | null;
}

interface VisitPdfParams extends Record<string, string | undefined> {
  search?: string;
}

// Converte datetime de ISO (yyyy-MM-dd'T'HH:mm:ss) para formato do backend (yyyy-MM-dd HH:mm:ss)
const convertDateTimeFormat = (dateTime: string): string => {
  return dateTime.replace("T", " ");
};

export const visitService = {
  async index({
    page,
    per_page,
    search,
    filters = {},
  }: VisitsGetRequestParams) {
    const response = await api.get<DefaultResponse<UnifiedVisit>>("/visits", {
      params: {
        page,
        per_page,
        search,
        ...filters,
      },
    });

    return response.data;
  },

  async show(id: number) {
    const response = await api.get<DefaultResponse<ManualVisit, false>>(
      `/visits/${id}`,
    );
    return response.data;
  },

  async create(data: VisitInput) {
    const payload = {
      reference_point_id: data.reference_point_id,
      visit_objective_id: data.visit_objective_id || null,
      start_datetime: convertDateTimeFormat(data.start_datetime),
      end_datetime: convertDateTimeFormat(data.end_datetime),
      notes: data.notes || null,
    };
    const response = await api.post("/visits", payload);
    return response.data;
  },

  async update(id: number | string, data: VisitInput) {
    const payload = {
      reference_point_id: data.reference_point_id,
      visit_objective_id: data.visit_objective_id || null,
      start_datetime: convertDateTimeFormat(data.start_datetime),
      end_datetime: convertDateTimeFormat(data.end_datetime),
      notes: data.notes || null,
    };
    const response = await api.put(`/visits/${id}`, payload);
    return response.data;
  },

  async destroy(id: number) {
    const response = await api.delete(`/visits/${id}`);
    return response.data;
  },

  async buildPdf(params: VisitPdfParams) {
    const response = await api.get("/visits/pdf", {
      params,
      responseType: "blob",
    });
    return response.data;
  },

  async updateObjective(
    type: "manual" | "automatic",
    id: number | string,
    payload: UpdateVisitObjectivePayload,
  ) {
    const response = await api.patch(
      `/visits/${type}/${id}/objective`,
      payload,
    );
    return response.data;
  },
};
